import { type MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { observable } from "mobx";

import {
  apiGetPatients,
  apiPutPatientsPatientId,
  ListPatientsResponse,
  PatientBaseSchema,
  PatientMonitoringType,
  PatientStatus,
  PermissionBit,
} from "../../api";
import { hasPermission } from "../../constants/permissions";
import { DataModel } from "../../models/data-model";
import { consentOk } from "../../utils/data-utils";
import { Merge } from "../../utils/types";

export type FullPatientsItem = Merge<ListPatientsResponse["patients"][number]>;

export interface TabQuery {
  patient_status?: PatientStatus[];
  monitoring_type?: PatientMonitoringType[];
}

export class PatientsTabModel extends DataModel<FullPatientsItem> {
  readonly idAttr = "patient_id" as const;
  @observable accessor loading = false;
  private loaded = false;
  private abortController: AbortController | undefined;

  constructor(
    private readonly query: TabQuery,
    private readonly getAssignedOnly: () => boolean,
    permissions: PermissionBit[],
    userUUID: string,
  ) {
    super([], permissions, userUUID);
  }

  async loadIfNeeded() {
    if (this.loaded || this.loading) return;
    this.loading = true;
    this.abortController?.abort();
    const abort = new AbortController();
    this.abortController = abort;
    try {
      const res = await apiGetPatients({
        query: {
          load_type: "basic",
          paginate: false,
          ...(this.getAssignedOnly() ? { assigned_only: true } : {}),
          ...this.query,
        },
        signal: abort.signal,
      });
      if (abort.signal.aborted) return;
      this.updateData(res.data.patients);
      this.loaded = true;
    } catch (err) {
      if (abort.signal.aborted) return;
      throw err;
    } finally {
      if (!abort.signal.aborted) {
        this.loading = false;
      }
    }
  }

  invalidate() {
    this.abortController?.abort();
    this.loaded = false;
    this.loading = false;
  }

  async updatePatient(patId: number, field: object) {
    await apiPutPatientsPatientId({
      path: { patient_id: patId },
      body: field,
    });
    window.location.reload();
  }

  disabledPatLink(patient: PatientBaseSchema) {
    return (
      !consentOk(patient) &&
      !hasPermission(this.permissions ?? [], PermissionBit.BYPASS_CONSENT)
    );
  }
}

export interface TabDef {
  id: string;
  label: MessageDescriptor;
  requiredPermission?: PermissionBit;
  query: TabQuery;
}

export const TAB_DEFS: TabDef[] = [
  {
    id: "active",
    label: msg`Active`,
    query: {
      patient_status: [PatientStatus.active],
      monitoring_type: [PatientMonitoringType.self_measurement],
    },
  },
  {
    // Pre-first-measurement patients. They have no watch status, schedule or
    // alerts, so this tab is the only place they surface — hence both
    // monitoring types, so a research patient can't fall through every tab
    // (Research is `active`-only) while still awaiting its first measurement.
    id: "onboarded",
    label: msg`Onboarded`,
    query: {
      patient_status: [PatientStatus.onboarded],
      monitoring_type: [
        PatientMonitoringType.self_measurement,
        PatientMonitoringType.physician_supervision,
      ],
    },
  },
  {
    id: "research",
    label: msg`Research`,
    requiredPermission: PermissionBit.CLINICAL_STUDIES,
    query: {
      patient_status: [PatientStatus.active],
      monitoring_type: [PatientMonitoringType.physician_supervision],
    },
  },
  {
    id: "not_active",
    label: msg`Not Active`,
    query: {
      patient_status: [
        PatientStatus.not_active,
        PatientStatus.archived,
        PatientStatus.deceased,
      ],
      monitoring_type: [
        PatientMonitoringType.self_measurement,
        PatientMonitoringType.physician_supervision,
      ],
    },
  },
  {
    id: "test",
    label: msg`Test`,
    requiredPermission: PermissionBit.GROUP_ADMIN_ROUTERS,
    query: {
      monitoring_type: [PatientMonitoringType.testing],
    },
  },
];
